import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth, API } from "../App";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { Briefcase, User, Zap, ArrowRight, Building2, Search } from "lucide-react";

const RoleSelection = () => {
  const navigate = useNavigate();
  const { user, setUser } = useAuth();
  const [selectedRole, setSelectedRole] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleContinue = async () => {
    if (!selectedRole) {
      toast.error("Please select a role");
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API}/auth/role`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ role: selectedRole }),
      });

      if (!response.ok) throw new Error("Failed to set role");

      const updatedUser = await response.json();
      setUser(updatedUser);

      if (selectedRole === "recruiter") {
        // Redirect recruiters to subscription page to choose a plan/start trial
        navigate("/subscription", { 
          state: { 
            isNewRecruiter: true,
            message: "Choose your plan to start posting jobs and finding candidates" 
          }
        });
      } else {
        navigate("/profile/build");
      }
    } catch (error) {
      toast.error("Failed to set role. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-obsidian flex flex-col items-center justify-center px-6 py-12">
      <div className="max-w-4xl w-full">
        {/* Header */}
        <motion.div 
          className="text-center mb-12"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="inline-flex items-center gap-2 mb-6">
            <div className="w-10 h-10 bg-crimson flex items-center justify-center">
              <Zap className="w-6 h-6 text-white" />
            </div>
            <span className="font-heading text-2xl font-bold">REKRUTOR</span>
          </div>
          <h1 className="font-heading text-4xl md:text-5xl font-bold mb-4">
            CHOOSE YOUR <span className="text-crimson">PATH</span>
          </h1>
          <p className="text-gray-400 text-lg">
            Welcome, {user?.name}. How will you use Rekrutor?
          </p>
        </motion.div>

        {/* Role Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
          <motion.button
            onClick={() => setSelectedRole("recruiter")}
            className={`p-8 border-2 transition-all text-left ${
              selectedRole === "recruiter"
                ? "border-crimson bg-crimson/10"
                : "border-[#222] bg-obsidian-light hover:border-crimson/50"
            }`}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1 }}
            data-testid="role-recruiter-btn"
          >
            <div className={`w-16 h-16 flex items-center justify-center mb-6 ${
              selectedRole === "recruiter" ? "bg-crimson" : "bg-[#1a1a1a]"
            }`}>
              <Building2 className="w-8 h-8 text-white" />
            </div>
            <h3 className="font-heading text-2xl font-bold mb-3">I'M A RECRUITER</h3>
            <p className="text-gray-400 mb-6">
              Post jobs, find talent, and streamline your hiring process with AI-powered tools.
            </p>
            <ul className="space-y-2 text-sm text-gray-300">
              <li className="flex items-center gap-2">
                <div className="w-1 h-1 bg-crimson" />
                AI-generated job descriptions
              </li>
              <li className="flex items-center gap-2">
                <div className="w-1 h-1 bg-crimson" />
                Smart candidate matching
              </li>
              <li className="flex items-center gap-2">
                <div className="w-1 h-1 bg-crimson" />
                AI-powered screening interviews
              </li>
            </ul>
          </motion.button>

          <motion.button
            onClick={() => setSelectedRole("jobseeker")}
            className={`p-8 border-2 transition-all text-left ${
              selectedRole === "jobseeker"
                ? "border-crimson bg-crimson/10"
                : "border-[#222] bg-obsidian-light hover:border-crimson/50"
            }`}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
            data-testid="role-jobseeker-btn"
          >
            <div className={`w-16 h-16 flex items-center justify-center mb-6 ${
              selectedRole === "jobseeker" ? "bg-crimson" : "bg-[#1a1a1a]"
            }`}>
              <Search className="w-8 h-8 text-white" />
            </div>
            <h3 className="font-heading text-2xl font-bold mb-3">I'M A JOB SEEKER</h3>
            <p className="text-gray-400 mb-6">
              Build your profile, get matched with opportunities, and accelerate your career pivot.
            </p>
            <ul className="space-y-2 text-sm text-gray-300">
              <li className="flex items-center gap-2">
                <div className="w-1 h-1 bg-crimson" />
                Smart profile from LinkedIn/Resume
              </li>
              <li className="flex items-center gap-2">
                <div className="w-1 h-1 bg-crimson" />
                Personalized job recommendations
              </li>
              <li className="flex items-center gap-2">
                <div className="w-1 h-1 bg-crimson" />
                AI career insights & coaching
              </li>
            </ul>
          </motion.button>
        </div>

        {/* Continue Button */}
        <motion.div 
          className="text-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
        >
          <Button
            onClick={handleContinue}
            disabled={!selectedRole || loading}
            className="bg-crimson hover:bg-crimson-hover text-white font-bold uppercase tracking-wider px-12 py-6 text-lg disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="role-continue-btn"
          >
            {loading ? (
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <>
                Continue <ArrowRight className="ml-2 w-5 h-5" />
              </>
            )}
          </Button>
        </motion.div>
      </div>
    </div>
  );
};

export default RoleSelection;
